/**
 * Query dispatch — the routing brain behind the search box.
 *
 * Five branches, in the legacy order (the order matters: an exact identifier must win
 * before filter parsing, or a ticker containing a corridor word would be mis-routed):
 *   1. direct cache hit on any identifier
 *   2. comma-separated multi-asset list
 *   3. filter intent, if the parse found any
 *   4. otherwise off-cache: score it via the backend, or show the off-universe card
 *   5. filter results render as single / list / off-universe by count
 */
import type { Asset, Filters } from '../core/types.ts';
import { applyFilters, findAsset, hasFilterIntent, parsePrompt, splitMultiQuery } from '../core/search.ts';

export type DispatchResult =
  | { kind: 'single'; asset: Asset }
  | { kind: 'multi'; matches: Asset[]; misses: string[]; query: string }
  | { kind: 'list'; assets: Asset[]; query: string; filters: Filters }
  | { kind: 'off-cache'; query: string }
  | { kind: 'off-universe'; query: string }
  | { kind: 'empty' };

/**
 * Decide what a query should render. Pure: no DOM, no network — so the whole routing
 * table is unit-testable, which it was not in the legacy build.
 */
export function dispatch(raw: string, assets: readonly Asset[], backendAvailable: boolean): DispatchResult {
  const q = (raw || '').trim();
  if (!q) return { kind: 'empty' };

  const direct = findAsset(assets, q);
  if (direct) return { kind: 'single', asset: direct };

  if (q.includes(',')) {
    const tokens = splitMultiQuery(q);
    const matches: Asset[] = [];
    const misses: string[] = [];
    for (const t of tokens) {
      const a = findAsset(assets, t);
      if (a) matches.push(a);
      else misses.push(t);
    }
    if (matches.length || misses.length) return { kind: 'multi', matches, misses, query: q };
  }

  const filters = parsePrompt(q);
  if (!hasFilterIntent(filters)) {
    return backendAvailable ? { kind: 'off-cache', query: q } : { kind: 'off-universe', query: q };
  }

  const filtered = applyFilters(assets, filters);
  if (filtered.length === 0) return { kind: 'off-universe', query: q };
  if (filtered.length === 1 && !filters.isListIntent) return { kind: 'single', asset: filtered[0]! };
  return { kind: 'list', assets: filtered, query: q, filters };
}
